import type { TranslationLanguage } from '../../ts/types';

/**
 * User-visible permission-dialog strings, localised per canonical
 * TranslationLanguage. Keys cover:
 * - the "X was previously denied" status sentence for camera / microphone
 *   / both
 * - the "Please check your browser settings to reset ..." follow-up
 *   instruction for each of the three cases
 * - the Firefox Android warning that renders in place of the reset
 *   instruction when that specific browser family is detected
 *
 * Consumers should go through `getPermissionResetInstructions(video,
 * audio, lang)` and `getFirefoxAndroidWarning(lang)` in permission-checker
 * rather than reading this dict directly.
 */
export interface PermissionStateTranslation {
  cameraDenied: string;
  microphoneDenied: string;
  bothDenied: string;
  resetCameraAndMic: string;
  resetCamera: string;
  resetMic: string;
  firefoxAndroidWarning: string;
}

export const PERMISSION_STATE_TRANSLATION = {
  af: {
    cameraDenied: 'Toegang tot die kamera is voorheen geweier.',
    microphoneDenied: 'Toegang tot die mikrofoon is voorheen geweier.',
    bothDenied: 'Toegang tot die kamera en mikrofoon is voorheen geweier.',
    resetCameraAndMic:
      'Kontroleer asseblief jou blaaier-instellings om kamera- en mikrofoontoestemmings terug te stel, en herlaai dan die bladsy.',
    resetCamera:
      'Kontroleer asseblief jou blaaier-instellings om kameratoestemmings terug te stel, en herlaai dan die bladsy.',
    resetMic:
      'Kontroleer asseblief jou blaaier-instellings om mikrofoontoestemmings terug te stel, en herlaai dan die bladsy.',
    firefoxAndroidWarning:
      'Firefox Android het bekende probleme met die kamera en mikrofoon. Gebruik asseblief Chrome of \'n ander blaaier vir die beste ervaring.',
  },
  am: {
    cameraDenied: 'የካሜራ መዳረሻ ቀደም ሲል ተከልክሏል።',
    microphoneDenied: 'የማይክሮፎን መዳረሻ ቀደም ሲል ተከልክሏል።',
    bothDenied: 'የካሜራ እና ማይክሮፎን መዳረሻ ቀደም ሲል ተከልክሏል።',
    resetCameraAndMic:
      'እባክዎ የካሜራ እና ማይክሮፎን ፈቃዶችን ዳግም ለማስጀመር የአሳሽዎን ቅንብሮች ይመልከቱ፣ ከዚያም ገጹን እንደገና ይጫኑ።',
    resetCamera:
      'እባክዎ የካሜራ ፈቃዶችን ዳግም ለማስጀመር የአሳሽዎን ቅንብሮች ይመልከቱ፣ ከዚያም ገጹን እንደገና ይጫኑ።',
    resetMic:
      'እባክዎ የማይክሮፎን ፈቃዶችን ዳግም ለማስጀመር የአሳሽዎን ቅንብሮች ይመልከቱ፣ ከዚያም ገጹን እንደገና ይጫኑ።',
    firefoxAndroidWarning:
      'Firefox Android በካሜራ እና ማይክሮፎን ላይ የታወቁ ችግሮች አሉት። ለተሻለ ተሞክሮ እባክዎ Chrome ወይም ሌላ አሳሽ ይጠቀሙ።',
  },
  ar: {
    cameraDenied: 'تم رفض الوصول إلى الكاميرا سابقًا.',
    microphoneDenied: 'تم رفض الوصول إلى الميكروفون سابقًا.',
    bothDenied: 'تم رفض الوصول إلى الكاميرا والميكروفون سابقًا.',
    resetCameraAndMic:
      'يرجى مراجعة إعدادات المتصفح لإعادة ضبط أذونات الكاميرا والميكروفون، ثم تحديث الصفحة.',
    resetCamera: 'يرجى مراجعة إعدادات المتصفح لإعادة ضبط أذونات الكاميرا، ثم تحديث الصفحة.',
    resetMic: 'يرجى مراجعة إعدادات المتصفح لإعادة ضبط أذونات الميكروفون، ثم تحديث الصفحة.',
    firefoxAndroidWarning:
      'يعاني Firefox Android من مشكلات معروفة في الكاميرا والميكروفون. للحصول على أفضل تجربة، يرجى استخدام Chrome أو متصفح آخر.',
  },
  hy: {
    cameraDenied: 'Տեսախցիկի հասանելիությունը նախկինում մերժվել է։',
    microphoneDenied: 'Խոսափողի հասանելիությունը նախկինում մերժվել է։',
    bothDenied: 'Տեսախցիկի և խոսափողի հասանելիությունը նախկինում մերժվել է։',
    resetCameraAndMic:
      'Խնդրում ենք ստուգել ձեր բրաուզերի կարգավորումները՝ տեսախցիկի և խոսափողի թույլտվությունները վերակայելու համար, այնուհետև թարմացնել էջը։',
    resetCamera:
      'Խնդրում ենք ստուգել ձեր բրաուզերի կարգավորումները՝ տեսախցիկի թույլտվությունները վերակայելու համար, այնուհետև թարմացնել էջը։',
    resetMic:
      'Խնդրում ենք ստուգել ձեր բրաուզերի կարգավորումները՝ խոսափողի թույլտվությունները վերակայելու համար, այնուհետև թարմացնել էջը։',
    firefoxAndroidWarning:
      'Firefox Android-ը ունի տեսախցիկի և խոսափողի հետ կապված հայտնի խնդիրներ։ Լավագույն փորձի համար խնդրում ենք օգտագործել Chrome կամ այլ բրաուզեր։',
  },
  az: {
    cameraDenied: 'Kameraya giriş əvvəlcədən rədd edilib.',
    microphoneDenied: 'Mikrofona giriş əvvəlcədən rədd edilib.',
    bothDenied: 'Kameraya və mikrofona giriş əvvəlcədən rədd edilib.',
    resetCameraAndMic:
      'Kamera və mikrofon icazələrini sıfırlamaq üçün brauzer parametrlərinizi yoxlayın, sonra səhifəni yeniləyin.',
    resetCamera:
      'Kamera icazələrini sıfırlamaq üçün brauzer parametrlərinizi yoxlayın, sonra səhifəni yeniləyin.',
    resetMic:
      'Mikrofon icazələrini sıfırlamaq üçün brauzer parametrlərinizi yoxlayın, sonra səhifəni yeniləyin.',
    firefoxAndroidWarning:
      'Firefox Android-də kamera və mikrofonla bağlı məlum problemlər var. Ən yaxşı təcrübə üçün Chrome və ya başqa brauzerdən istifadə edin.',
  },
  be: {
    cameraDenied: 'Доступ да камеры быў раней забаронены.',
    microphoneDenied: 'Доступ да мікрафона быў раней забаронены.',
    bothDenied: 'Доступ да камеры і мікрафона быў раней забаронены.',
    resetCameraAndMic:
      'Калі ласка, праверце налады вашага браўзера, каб скінуць дазволы для камеры і мікрафона, а потым абнавіце старонку.',
    resetCamera:
      'Калі ласка, праверце налады вашага браўзера, каб скінуць дазволы для камеры, а потым абнавіце старонку.',
    resetMic:
      'Калі ласка, праверце налады вашага браўзера, каб скінуць дазволы для мікрафона, а потым абнавіце старонку.',
    firefoxAndroidWarning:
      'Firefox Android мае вядомыя праблемы з камерай і мікрафонам. Для найлепшага вопыту, калі ласка, выкарыстоўвайце Chrome або іншы браўзер.',
  },
  bn: {
    cameraDenied: 'ক্যামেরা অ্যাক্সেস আগে প্রত্যাখ্যান করা হয়েছিল।',
    microphoneDenied: 'মাইক্রোফোন অ্যাক্সেস আগে প্রত্যাখ্যান করা হয়েছিল।',
    bothDenied: 'ক্যামেরা এবং মাইক্রোফোন অ্যাক্সেস আগে প্রত্যাখ্যান করা হয়েছিল।',
    resetCameraAndMic:
      'অনুগ্রহ করে ক্যামেরা এবং মাইক্রোফোনের অনুমতি রিসেট করতে আপনার ব্রাউজার সেটিংস পরীক্ষা করুন, তারপর পৃষ্ঠাটি রিফ্রেশ করুন।',
    resetCamera:
      'অনুগ্রহ করে ক্যামেরার অনুমতি রিসেট করতে আপনার ব্রাউজার সেটিংস পরীক্ষা করুন, তারপর পৃষ্ঠাটি রিফ্রেশ করুন।',
    resetMic:
      'অনুগ্রহ করে মাইক্রোফোনের অনুমতি রিসেট করতে আপনার ব্রাউজার সেটিংস পরীক্ষা করুন, তারপর পৃষ্ঠাটি রিফ্রেশ করুন।',
    firefoxAndroidWarning:
      'Firefox Android-এ ক্যামেরা এবং মাইক্রোফোন সংক্রান্ত পরিচিত সমস্যা রয়েছে। সেরা অভিজ্ঞতার জন্য, অনুগ্রহ করে Chrome বা অন্য কোনো ব্রাউজার ব্যবহার করুন।',
  },
  bs: {
    cameraDenied: 'Pristup kameri je prethodno odbijen.',
    microphoneDenied: 'Pristup mikrofonu je prethodno odbijen.',
    bothDenied: 'Pristup kameri i mikrofonu je prethodno odbijen.',
    resetCameraAndMic:
      'Molimo provjerite postavke preglednika da biste resetovali dozvole za kameru i mikrofon, a zatim osvježite stranicu.',
    resetCamera:
      'Molimo provjerite postavke preglednika da biste resetovali dozvole za kameru, a zatim osvježite stranicu.',
    resetMic:
      'Molimo provjerite postavke preglednika da biste resetovali dozvole za mikrofon, a zatim osvježite stranicu.',
    firefoxAndroidWarning:
      'Firefox Android ima poznate probleme s kamerom i mikrofonom. Za najbolje iskustvo, molimo koristite Chrome ili neki drugi preglednik.',
  },
  bg: {
    cameraDenied: 'Достъпът до камерата беше отказан преди това.',
    microphoneDenied: 'Достъпът до микрофона беше отказан преди това.',
    bothDenied: 'Достъпът до камерата и микрофона беше отказан преди това.',
    resetCameraAndMic:
      'Моля, проверете настройките на браузъра си, за да нулирате разрешенията за камера и микрофон, след което опреснете страницата.',
    resetCamera:
      'Моля, проверете настройките на браузъра си, за да нулирате разрешенията за камера, след което опреснете страницата.',
    resetMic:
      'Моля, проверете настройките на браузъра си, за да нулирате разрешенията за микрофон, след което опреснете страницата.',
    firefoxAndroidWarning:
      'Firefox Android има известни проблеми с камерата и микрофона. За най-добро изживяване, моля, използвайте Chrome или друг браузър.',
  },
  yue: {
    cameraDenied: '之前已經拒絕咗鏡頭嘅存取權限。',
    microphoneDenied: '之前已經拒絕咗咪高峰嘅存取權限。',
    bothDenied: '之前已經拒絕咗鏡頭同咪高峰嘅存取權限。',
    resetCameraAndMic: '麻煩你去瀏覽器設定度重設鏡頭同咪高峰嘅權限，然後再重新整理個版。',
    resetCamera: '麻煩你去瀏覽器設定度重設鏡頭嘅權限，然後再重新整理個版。',
    resetMic: '麻煩你去瀏覽器設定度重設咪高峰嘅權限，然後再重新整理個版。',
    firefoxAndroidWarning:
      'Firefox Android 喺鏡頭同咪高峰方面有已知嘅問題。為咗有最好嘅體驗，麻煩你用 Chrome 或者其他瀏覽器。',
  },
  ca: {
    cameraDenied: 'L\'accés a la càmera s\'ha denegat anteriorment.',
    microphoneDenied: 'L\'accés al micròfon s\'ha denegat anteriorment.',
    bothDenied: 'L\'accés a la càmera i al micròfon s\'ha denegat anteriorment.',
    resetCameraAndMic:
      'Comproveu la configuració del navegador per restablir els permisos de la càmera i el micròfon, i després torneu a carregar la pàgina.',
    resetCamera:
      'Comproveu la configuració del navegador per restablir els permisos de la càmera, i després torneu a carregar la pàgina.',
    resetMic:
      'Comproveu la configuració del navegador per restablir els permisos del micròfon, i després torneu a carregar la pàgina.',
    firefoxAndroidWarning:
      'Firefox Android té problemes coneguts amb la càmera i el micròfon. Per a una millor experiència, utilitzeu Chrome o un altre navegador.',
  },
  zh: {
    cameraDenied: '之前已拒绝摄像头访问权限。',
    microphoneDenied: '之前已拒绝麦克风访问权限。',
    bothDenied: '之前已拒绝摄像头和麦克风访问权限。',
    resetCameraAndMic: '请检查您的浏览器设置以重置摄像头和麦克风权限，然后刷新页面。',
    resetCamera: '请检查您的浏览器设置以重置摄像头权限，然后刷新页面。',
    resetMic: '请检查您的浏览器设置以重置麦克风权限，然后刷新页面。',
    firefoxAndroidWarning:
      'Firefox Android 存在已知的摄像头和麦克风问题。为获得最佳体验，请使用 Chrome 或其他浏览器。',
  },
  'zh-TW': {
    cameraDenied: '先前已拒絕攝影機存取權限。',
    microphoneDenied: '先前已拒絕麥克風存取權限。',
    bothDenied: '先前已拒絕攝影機和麥克風存取權限。',
    resetCameraAndMic: '請檢查您的瀏覽器設定以重設攝影機和麥克風權限，然後重新整理頁面。',
    resetCamera: '請檢查您的瀏覽器設定以重設攝影機權限，然後重新整理頁面。',
    resetMic: '請檢查您的瀏覽器設定以重設麥克風權限，然後重新整理頁面。',
    firefoxAndroidWarning:
      'Firefox Android 有已知的攝影機和麥克風問題。為獲得最佳體驗，請使用 Chrome 或其他瀏覽器。',
  },
  hr: {
    cameraDenied: 'Pristup kameri prethodno je odbijen.',
    microphoneDenied: 'Pristup mikrofonu prethodno je odbijen.',
    bothDenied: 'Pristup kameri i mikrofonu prethodno je odbijen.',
    resetCameraAndMic:
      'Molimo provjerite postavke preglednika kako biste resetirali dopuštenja za kameru i mikrofon, a zatim osvježite stranicu.',
    resetCamera:
      'Molimo provjerite postavke preglednika kako biste resetirali dopuštenja za kameru, a zatim osvježite stranicu.',
    resetMic:
      'Molimo provjerite postavke preglednika kako biste resetirali dopuštenja za mikrofon, a zatim osvježite stranicu.',
    firefoxAndroidWarning:
      'Firefox Android ima poznate probleme s kamerom i mikrofonom. Za najbolje iskustvo, molimo koristite Chrome ili neki drugi preglednik.',
  },
  cs: {
    cameraDenied: 'Přístup ke kameře byl dříve zamítnut.',
    microphoneDenied: 'Přístup k mikrofonu byl dříve zamítnut.',
    bothDenied: 'Přístup ke kameře a mikrofonu byl dříve zamítnut.',
    resetCameraAndMic:
      'Zkontrolujte prosím nastavení prohlížeče a resetujte oprávnění pro kameru a mikrofon, poté stránku obnovte.',
    resetCamera:
      'Zkontrolujte prosím nastavení prohlížeče a resetujte oprávnění pro kameru, poté stránku obnovte.',
    resetMic:
      'Zkontrolujte prosím nastavení prohlížeče a resetujte oprávnění pro mikrofon, poté stránku obnovte.',
    firefoxAndroidWarning:
      'Firefox Android má známé problémy s kamerou a mikrofonem. Pro nejlepší zážitek použijte prosím Chrome nebo jiný prohlížeč.',
  },
  da: {
    cameraDenied: 'Adgang til kameraet blev tidligere afvist.',
    microphoneDenied: 'Adgang til mikrofonen blev tidligere afvist.',
    bothDenied: 'Adgang til kamera og mikrofon blev tidligere afvist.',
    resetCameraAndMic:
      'Tjek venligst dine browserindstillinger for at nulstille kamera- og mikrofontilladelser, og opdater derefter siden.',
    resetCamera:
      'Tjek venligst dine browserindstillinger for at nulstille kameratilladelser, og opdater derefter siden.',
    resetMic:
      'Tjek venligst dine browserindstillinger for at nulstille mikrofontilladelser, og opdater derefter siden.',
    firefoxAndroidWarning:
      'Firefox Android har kendte problemer med kamera og mikrofon. For den bedste oplevelse, brug venligst Chrome eller en anden browser.',
  },
  nl: {
    cameraDenied: 'Cameratoegang is eerder geweigerd.',
    microphoneDenied: 'Microfoontoegang is eerder geweigerd.',
    bothDenied: 'Camera- en microfoontoegang zijn eerder geweigerd.',
    resetCameraAndMic:
      'Controleer je browserinstellingen om de camera- en microfoonrechten te resetten en ververs vervolgens de pagina.',
    resetCamera:
      'Controleer je browserinstellingen om de camerarechten te resetten en ververs vervolgens de pagina.',
    resetMic:
      'Controleer je browserinstellingen om de microfoonrechten te resetten en ververs vervolgens de pagina.',
    firefoxAndroidWarning:
      'Firefox Android heeft bekende problemen met camera en microfoon. Gebruik voor de beste ervaring Chrome of een andere browser.',
  },
  en: {
    cameraDenied: 'Camera access was previously denied.',
    microphoneDenied: 'Microphone access was previously denied.',
    bothDenied: 'Camera and microphone access were previously denied.',
    resetCameraAndMic:
      'Please check your browser settings to reset camera and microphone permissions, then refresh the page.',
    resetCamera:
      'Please check your browser settings to reset camera permissions, then refresh the page.',
    resetMic:
      'Please check your browser settings to reset microphone permissions, then refresh the page.',
    firefoxAndroidWarning:
      'Firefox Android has known camera and microphone issues. For the best experience, please use Chrome or another browser.',
  },
  et: {
    cameraDenied: 'Kaamera kasutamine on varem keelatud.',
    microphoneDenied: 'Mikrofoni kasutamine on varem keelatud.',
    bothDenied: 'Kaamera ja mikrofoni kasutamine on varem keelatud.',
    resetCameraAndMic:
      'Palun kontrollige oma brauseri seadeid, et lähtestada kaamera ja mikrofoni load, seejärel värskendage lehte.',
    resetCamera:
      'Palun kontrollige oma brauseri seadeid, et lähtestada kaamera load, seejärel värskendage lehte.',
    resetMic:
      'Palun kontrollige oma brauseri seadeid, et lähtestada mikrofoni load, seejärel värskendage lehte.',
    firefoxAndroidWarning:
      'Firefox Androidil on teadaolevaid probleeme kaamera ja mikrofoniga. Parima kogemuse saamiseks kasutage palun Chrome\'i või mõnda muud brauserit.',
  },
  fi: {
    cameraDenied: 'Kameran käyttö on aiemmin estetty.',
    microphoneDenied: 'Mikrofonin käyttö on aiemmin estetty.',
    bothDenied: 'Kameran ja mikrofonin käyttö on aiemmin estetty.',
    resetCameraAndMic:
      'Tarkista selaimesi asetukset nollataksesi kameran ja mikrofonin käyttöoikeudet ja päivitä sitten sivu.',
    resetCamera:
      'Tarkista selaimesi asetukset nollataksesi kameran käyttöoikeudet ja päivitä sitten sivu.',
    resetMic:
      'Tarkista selaimesi asetukset nollataksesi mikrofonin käyttöoikeudet ja päivitä sitten sivu.',
    firefoxAndroidWarning:
      'Firefox Androidissa on tunnettuja kamera- ja mikrofoniongelmia. Parhaan kokemuksen saat käyttämällä Chromea tai jotakin muuta selainta.',
  },
  fr: {
    cameraDenied: 'L\'accès à la caméra a été refusé précédemment.',
    microphoneDenied: 'L\'accès au microphone a été refusé précédemment.',
    bothDenied: 'L\'accès à la caméra et au microphone a été refusé précédemment.',
    resetCameraAndMic:
      'Veuillez vérifier les paramètres de votre navigateur pour réinitialiser les autorisations de la caméra et du microphone, puis actualisez la page.',
    resetCamera:
      'Veuillez vérifier les paramètres de votre navigateur pour réinitialiser les autorisations de la caméra, puis actualisez la page.',
    resetMic:
      'Veuillez vérifier les paramètres de votre navigateur pour réinitialiser les autorisations du microphone, puis actualisez la page.',
    firefoxAndroidWarning:
      'Firefox Android présente des problèmes connus avec la caméra et le microphone. Pour une expérience optimale, veuillez utiliser Chrome ou un autre navigateur.',
  },
  gl: {
    cameraDenied: 'O acceso á cámara foi denegado previamente.',
    microphoneDenied: 'O acceso ao micrófono foi denegado previamente.',
    bothDenied: 'O acceso á cámara e ao micrófono foi denegado previamente.',
    resetCameraAndMic:
      'Comproba a configuración do teu navegador para restablecer os permisos da cámara e do micrófono e, a continuación, actualiza a páxina.',
    resetCamera:
      'Comproba a configuración do teu navegador para restablecer os permisos da cámara e, a continuación, actualiza a páxina.',
    resetMic:
      'Comproba a configuración do teu navegador para restablecer os permisos do micrófono e, a continuación, actualiza a páxina.',
    firefoxAndroidWarning:
      'Firefox Android ten problemas coñecidos coa cámara e o micrófono. Para unha mellor experiencia, usa Chrome ou outro navegador.',
  },
  de: {
    cameraDenied: 'Der Kamerazugriff wurde zuvor verweigert.',
    microphoneDenied: 'Der Mikrofonzugriff wurde zuvor verweigert.',
    bothDenied: 'Der Kamera- und Mikrofonzugriff wurde zuvor verweigert.',
    resetCameraAndMic:
      'Bitte überprüfen Sie Ihre Browsereinstellungen, um die Kamera- und Mikrofonberechtigungen zurückzusetzen, und aktualisieren Sie anschließend die Seite.',
    resetCamera:
      'Bitte überprüfen Sie Ihre Browsereinstellungen, um die Kameraberechtigungen zurückzusetzen, und aktualisieren Sie anschließend die Seite.',
    resetMic:
      'Bitte überprüfen Sie Ihre Browsereinstellungen, um die Mikrofonberechtigungen zurückzusetzen, und aktualisieren Sie anschließend die Seite.',
    firefoxAndroidWarning:
      'Firefox Android hat bekannte Probleme mit Kamera und Mikrofon. Für das beste Erlebnis verwenden Sie bitte Chrome oder einen anderen Browser.',
  },
  el: {
    cameraDenied: 'Η πρόσβαση στην κάμερα είχε απορριφθεί προηγουμένως.',
    microphoneDenied: 'Η πρόσβαση στο μικρόφωνο είχε απορριφθεί προηγουμένως.',
    bothDenied: 'Η πρόσβαση στην κάμερα και στο μικρόφωνο είχε απορριφθεί προηγουμένως.',
    resetCameraAndMic:
      'Ελέγξτε τις ρυθμίσεις του προγράμματος περιήγησής σας για να επαναφέρετε τις άδειες της κάμερας και του μικροφώνου και, στη συνέχεια, ανανεώστε τη σελίδα.',
    resetCamera:
      'Ελέγξτε τις ρυθμίσεις του προγράμματος περιήγησής σας για να επαναφέρετε τις άδειες της κάμερας και, στη συνέχεια, ανανεώστε τη σελίδα.',
    resetMic:
      'Ελέγξτε τις ρυθμίσεις του προγράμματος περιήγησής σας για να επαναφέρετε τις άδειες του μικροφώνου και, στη συνέχεια, ανανεώστε τη σελίδα.',
    firefoxAndroidWarning:
      'Ο Firefox Android έχει γνωστά προβλήματα με την κάμερα και το μικρόφωνο. Για την καλύτερη εμπειρία, χρησιμοποιήστε τον Chrome ή κάποιο άλλο πρόγραμμα περιήγησης.',
  },
  iw: {
    cameraDenied: 'הגישה למצלמה נדחתה בעבר.',
    microphoneDenied: 'הגישה למיקרופון נדחתה בעבר.',
    bothDenied: 'הגישה למצלמה ולמיקרופון נדחתה בעבר.',
    resetCameraAndMic:
      'אנא בדוק את הגדרות הדפדפן שלך כדי לאפס את ההרשאות של המצלמה והמיקרופון, ולאחר מכן רענן את הדף.',
    resetCamera:
      'אנא בדוק את הגדרות הדפדפן שלך כדי לאפס את הרשאות המצלמה, ולאחר מכן רענן את הדף.',
    resetMic:
      'אנא בדוק את הגדרות הדפדפן שלך כדי לאפס את הרשאות המיקרופון, ולאחר מכן רענן את הדף.',
    firefoxAndroidWarning:
      'ל-Firefox Android יש בעיות ידועות עם המצלמה והמיקרופון. לחוויה הטובה ביותר, אנא השתמש ב-Chrome או בדפדפן אחר.',
  },
  hi: {
    cameraDenied: 'कैमरा एक्सेस पहले अस्वीकार किया गया था।',
    microphoneDenied: 'माइक्रोफ़ोन एक्सेस पहले अस्वीकार किया गया था।',
    bothDenied: 'कैमरा और माइक्रोफ़ोन एक्सेस पहले अस्वीकार किया गया था।',
    resetCameraAndMic:
      'कृपया कैमरा और माइक्रोफ़ोन अनुमतियाँ रीसेट करने के लिए अपनी ब्राउज़र सेटिंग्स जाँचें, फिर पेज को रीफ़्रेश करें।',
    resetCamera:
      'कृपया कैमरा अनुमतियाँ रीसेट करने के लिए अपनी ब्राउज़र सेटिंग्स जाँचें, फिर पेज को रीफ़्रेश करें।',
    resetMic:
      'कृपया माइक्रोफ़ोन अनुमतियाँ रीसेट करने के लिए अपनी ब्राउज़र सेटिंग्स जाँचें, फिर पेज को रीफ़्रेश करें।',
    firefoxAndroidWarning:
      'Firefox Android में कैमरा और माइक्रोफ़ोन की ज्ञात समस्याएँ हैं। सर्वोत्तम अनुभव के लिए, कृपया Chrome या किसी अन्य ब्राउज़र का उपयोग करें।',
  },
  hu: {
    cameraDenied: 'A kamerához való hozzáférést korábban elutasították.',
    microphoneDenied: 'A mikrofonhoz való hozzáférést korábban elutasították.',
    bothDenied: 'A kamerához és a mikrofonhoz való hozzáférést korábban elutasították.',
    resetCameraAndMic:
      'Kérjük, ellenőrizze a böngésző beállításait a kamera és a mikrofon engedélyeinek visszaállításához, majd frissítse az oldalt.',
    resetCamera:
      'Kérjük, ellenőrizze a böngésző beállításait a kamera engedélyeinek visszaállításához, majd frissítse az oldalt.',
    resetMic:
      'Kérjük, ellenőrizze a böngésző beállításait a mikrofon engedélyeinek visszaállításához, majd frissítse az oldalt.',
    firefoxAndroidWarning:
      'A Firefox Android ismert problémákkal küzd a kamera és a mikrofon terén. A legjobb élmény érdekében kérjük, használja a Chrome-ot vagy egy másik böngészőt.',
  },
  is: {
    cameraDenied: 'Aðgangi að myndavél var áður hafnað.',
    microphoneDenied: 'Aðgangi að hljóðnema var áður hafnað.',
    bothDenied: 'Aðgangi að myndavél og hljóðnema var áður hafnað.',
    resetCameraAndMic:
      'Vinsamlegast athugaðu stillingar vafrans þíns til að endurstilla heimildir fyrir myndavél og hljóðnema og endurhladdu síðan síðuna.',
    resetCamera:
      'Vinsamlegast athugaðu stillingar vafrans þíns til að endurstilla heimildir fyrir myndavél og endurhladdu síðan síðuna.',
    resetMic:
      'Vinsamlegast athugaðu stillingar vafrans þíns til að endurstilla heimildir fyrir hljóðnema og endurhladdu síðan síðuna.',
    firefoxAndroidWarning:
      'Firefox Android er með þekkt vandamál varðandi myndavél og hljóðnema. Fyrir bestu upplifun, vinsamlegast notaðu Chrome eða annan vafra.',
  },
  it: {
    cameraDenied: 'L\'accesso alla fotocamera è stato precedentemente negato.',
    microphoneDenied: 'L\'accesso al microfono è stato precedentemente negato.',
    bothDenied: 'L\'accesso alla fotocamera e al microfono è stato precedentemente negato.',
    resetCameraAndMic:
      'Controlla le impostazioni del browser per reimpostare le autorizzazioni della fotocamera e del microfono, quindi aggiorna la pagina.',
    resetCamera:
      'Controlla le impostazioni del browser per reimpostare le autorizzazioni della fotocamera, quindi aggiorna la pagina.',
    resetMic:
      'Controlla le impostazioni del browser per reimpostare le autorizzazioni del microfono, quindi aggiorna la pagina.',
    firefoxAndroidWarning:
      'Firefox Android presenta problemi noti con fotocamera e microfono. Per un\'esperienza ottimale, utilizza Chrome o un altro browser.',
  },
  ja: {
    cameraDenied: 'カメラへのアクセスは以前に拒否されました。',
    microphoneDenied: 'マイクへのアクセスは以前に拒否されました。',
    bothDenied: 'カメラとマイクへのアクセスは以前に拒否されました。',
    resetCameraAndMic:
      'ブラウザの設定を確認してカメラとマイクの権限をリセットし、ページを再読み込みしてください。',
    resetCamera:
      'ブラウザの設定を確認してカメラの権限をリセットし、ページを再読み込みしてください。',
    resetMic:
      'ブラウザの設定を確認してマイクの権限をリセットし、ページを再読み込みしてください。',
    firefoxAndroidWarning:
      'Firefox Android にはカメラとマイクに関する既知の問題があります。最適な体験のために、Chrome またはその他のブラウザをご使用ください。',
  },
  ka: {
    cameraDenied: 'კამერაზე წვდომა ადრე უარყოფილი იყო.',
    microphoneDenied: 'მიკროფონზე წვდომა ადრე უარყოფილი იყო.',
    bothDenied: 'კამერასა და მიკროფონზე წვდომა ადრე უარყოფილი იყო.',
    resetCameraAndMic:
      'გთხოვთ, შეამოწმოთ თქვენი ბრაუზერის პარამეტრები კამერისა და მიკროფონის ნებართვების გადასაყენებლად და შემდეგ განაახლოთ გვერდი.',
    resetCamera:
      'გთხოვთ, შეამოწმოთ თქვენი ბრაუზერის პარამეტრები კამერის ნებართვების გადასაყენებლად და შემდეგ განაახლოთ გვერდი.',
    resetMic:
      'გთხოვთ, შეამოწმოთ თქვენი ბრაუზერის პარამეტრები მიკროფონის ნებართვების გადასაყენებლად და შემდეგ განაახლოთ გვერდი.',
    firefoxAndroidWarning:
      'Firefox Android-ს აქვს კამერისა და მიკროფონის ცნობილი პრობლემები. საუკეთესო გამოცდილებისთვის, გთხოვთ, გამოიყენოთ Chrome ან სხვა ბრაუზერი.',
  },
  kn: {
    cameraDenied: 'ಕ್ಯಾಮೆರಾ ಪ್ರವೇಶವನ್ನು ಈ ಹಿಂದೆ ನಿರಾಕರಿಸಲಾಗಿದೆ.',
    microphoneDenied: 'ಮೈಕ್ರೋಫೋನ್ ಪ್ರವೇಶವನ್ನು ಈ ಹಿಂದೆ ನಿರಾಕರಿಸಲಾಗಿದೆ.',
    bothDenied: 'ಕ್ಯಾಮೆರಾ ಮತ್ತು ಮೈಕ್ರೋಫೋನ್ ಪ್ರವೇಶವನ್ನು ಈ ಹಿಂದೆ ನಿರಾಕರಿಸಲಾಗಿದೆ.',
    resetCameraAndMic:
      'ಕ್ಯಾಮೆರಾ ಮತ್ತು ಮೈಕ್ರೋಫೋನ್ ಅನುಮತಿಗಳನ್ನು ಮರುಹೊಂದಿಸಲು ದಯವಿಟ್ಟು ನಿಮ್ಮ ಬ್ರೌಸರ್ ಸೆಟ್ಟಿಂಗ್‌ಗಳನ್ನು ಪರಿಶೀಲಿಸಿ, ನಂತರ ಪುಟವನ್ನು ರಿಫ್ರೆಶ್ ಮಾಡಿ.',
    resetCamera:
      'ಕ್ಯಾಮೆರಾ ಅನುಮತಿಗಳನ್ನು ಮರುಹೊಂದಿಸಲು ದಯವಿಟ್ಟು ನಿಮ್ಮ ಬ್ರೌಸರ್ ಸೆಟ್ಟಿಂಗ್‌ಗಳನ್ನು ಪರಿಶೀಲಿಸಿ, ನಂತರ ಪುಟವನ್ನು ರಿಫ್ರೆಶ್ ಮಾಡಿ.',
    resetMic:
      'ಮೈಕ್ರೋಫೋನ್ ಅನುಮತಿಗಳನ್ನು ಮರುಹೊಂದಿಸಲು ದಯವಿಟ್ಟು ನಿಮ್ಮ ಬ್ರೌಸರ್ ಸೆಟ್ಟಿಂಗ್‌ಗಳನ್ನು ಪರಿಶೀಲಿಸಿ, ನಂತರ ಪುಟವನ್ನು ರಿಫ್ರೆಶ್ ಮಾಡಿ.',
    firefoxAndroidWarning:
      'Firefox Android ನಲ್ಲಿ ಕ್ಯಾಮೆರಾ ಮತ್ತು ಮೈಕ್ರೋಫೋನ್‌ಗೆ ಸಂಬಂಧಿಸಿದ ತಿಳಿದಿರುವ ಸಮಸ್ಯೆಗಳಿವೆ. ಉತ್ತಮ ಅನುಭವಕ್ಕಾಗಿ, ದಯವಿಟ್ಟು Chrome ಅಥವಾ ಇನ್ನೊಂದು ಬ್ರೌಸರ್ ಅನ್ನು ಬಳಸಿ.',
  },
  kk: {
    cameraDenied: 'Камераға қол жеткізуге бұрын рұқсат берілмеген.',
    microphoneDenied: 'Микрофонға қол жеткізуге бұрын рұқсат берілмеген.',
    bothDenied: 'Камера мен микрофонға қол жеткізуге бұрын рұқсат берілмеген.',
    resetCameraAndMic:
      'Камера мен микрофон рұқсаттарын қалпына келтіру үшін браузер параметрлерін тексеріп, бетті жаңартыңыз.',
    resetCamera:
      'Камера рұқсаттарын қалпына келтіру үшін браузер параметрлерін тексеріп, бетті жаңартыңыз.',
    resetMic:
      'Микрофон рұқсаттарын қалпына келтіру үшін браузер параметрлерін тексеріп, бетті жаңартыңыз.',
    firefoxAndroidWarning:
      'Firefox Android-та камера мен микрофонға қатысты белгілі мәселелер бар. Ең жақсы тәжірибе үшін Chrome немесе басқа браузерді пайдаланыңыз.',
  },
  ko: {
    cameraDenied: '카메라 접근이 이전에 거부되었습니다.',
    microphoneDenied: '마이크 접근이 이전에 거부되었습니다.',
    bothDenied: '카메라와 마이크 접근이 이전에 거부되었습니다.',
    resetCameraAndMic:
      '브라우저 설정에서 카메라와 마이크 권한을 재설정한 후 페이지를 새로고침해 주세요.',
    resetCamera: '브라우저 설정에서 카메라 권한을 재설정한 후 페이지를 새로고침해 주세요.',
    resetMic: '브라우저 설정에서 마이크 권한을 재설정한 후 페이지를 새로고침해 주세요.',
    firefoxAndroidWarning:
      'Firefox Android에는 알려진 카메라 및 마이크 문제가 있습니다. 최상의 환경을 위해 Chrome 또는 다른 브라우저를 사용해 주세요.',
  },
  lv: {
    cameraDenied: 'Piekļuve kamerai iepriekš tika liegta.',
    microphoneDenied: 'Piekļuve mikrofonam iepriekš tika liegta.',
    bothDenied: 'Piekļuve kamerai un mikrofonam iepriekš tika liegta.',
    resetCameraAndMic:
      'Lūdzu, pārbaudiet pārlūka iestatījumus, lai atiestatītu kameras un mikrofona atļaujas, un pēc tam atsvaidziniet lapu.',
    resetCamera:
      'Lūdzu, pārbaudiet pārlūka iestatījumus, lai atiestatītu kameras atļaujas, un pēc tam atsvaidziniet lapu.',
    resetMic:
      'Lūdzu, pārbaudiet pārlūka iestatījumus, lai atiestatītu mikrofona atļaujas, un pēc tam atsvaidziniet lapu.',
    firefoxAndroidWarning:
      'Firefox Android ir zināmas problēmas ar kameru un mikrofonu. Lai nodrošinātu labāku pieredzi, lūdzu, izmantojiet Chrome vai citu pārlūku.',
  },
  lt: {
    cameraDenied: 'Prieiga prie kameros anksčiau buvo uždrausta.',
    microphoneDenied: 'Prieiga prie mikrofono anksčiau buvo uždrausta.',
    bothDenied: 'Prieiga prie kameros ir mikrofono anksčiau buvo uždrausta.',
    resetCameraAndMic:
      'Patikrinkite naršyklės nustatymus, kad iš naujo nustatytumėte kameros ir mikrofono leidimus, tada atnaujinkite puslapį.',
    resetCamera:
      'Patikrinkite naršyklės nustatymus, kad iš naujo nustatytumėte kameros leidimus, tada atnaujinkite puslapį.',
    resetMic:
      'Patikrinkite naršyklės nustatymus, kad iš naujo nustatytumėte mikrofono leidimus, tada atnaujinkite puslapį.',
    firefoxAndroidWarning:
      'Firefox Android turi žinomų kameros ir mikrofono problemų. Kad patirtis būtų geriausia, naudokite Chrome arba kitą naršyklę.',
  },
  mk: {
    cameraDenied: 'Пристапот до камерата претходно беше одбиен.',
    microphoneDenied: 'Пристапот до микрофонот претходно беше одбиен.',
    bothDenied: 'Пристапот до камерата и микрофонот претходно беше одбиен.',
    resetCameraAndMic:
      'Ве молиме проверете ги поставките на прелистувачот за да ги ресетирате дозволите за камера и микрофон, а потоа освежете ја страницата.',
    resetCamera:
      'Ве молиме проверете ги поставките на прелистувачот за да ги ресетирате дозволите за камера, а потоа освежете ја страницата.',
    resetMic:
      'Ве молиме проверете ги поставките на прелистувачот за да ги ресетирате дозволите за микрофон, а потоа освежете ја страницата.',
    firefoxAndroidWarning:
      'Firefox Android има познати проблеми со камерата и микрофонот. За најдобро искуство, ве молиме користете Chrome или друг прелистувач.',
  },
  ms: {
    cameraDenied: 'Akses kamera telah dinafikan sebelum ini.',
    microphoneDenied: 'Akses mikrofon telah dinafikan sebelum ini.',
    bothDenied: 'Akses kamera dan mikrofon telah dinafikan sebelum ini.',
    resetCameraAndMic:
      'Sila semak tetapan pelayar anda untuk menetapkan semula kebenaran kamera dan mikrofon, kemudian muat semula halaman.',
    resetCamera:
      'Sila semak tetapan pelayar anda untuk menetapkan semula kebenaran kamera, kemudian muat semula halaman.',
    resetMic:
      'Sila semak tetapan pelayar anda untuk menetapkan semula kebenaran mikrofon, kemudian muat semula halaman.',
    firefoxAndroidWarning:
      'Firefox Android mempunyai masalah kamera dan mikrofon yang diketahui. Untuk pengalaman terbaik, sila gunakan Chrome atau pelayar lain.',
  },
  mi: {
    cameraDenied: 'I whakakāhoretia te urunga ki te kāmera i mua.',
    microphoneDenied: 'I whakakāhoretia te urunga ki te hopuoro i mua.',
    bothDenied: 'I whakakāhoretia te urunga ki te kāmera me te hopuoro i mua.',
    resetCameraAndMic:
      'Tēnā tirohia ngā tautuhinga o tō tirotiro kia tautuhia anōtia ngā whakaaetanga kāmera me te hopuoro, kātahi ka whakahou i te whārangi.',
    resetCamera:
      'Tēnā tirohia ngā tautuhinga o tō tirotiro kia tautuhia anōtia ngā whakaaetanga kāmera, kātahi ka whakahou i te whārangi.',
    resetMic:
      'Tēnā tirohia ngā tautuhinga o tō tirotiro kia tautuhia anōtia ngā whakaaetanga hopuoro, kātahi ka whakahou i te whārangi.',
    firefoxAndroidWarning:
      'He raru mōhiotia o Firefox Android mō te kāmera me te hopuoro. Mō te wheako pai rawa, tēnā whakamahia a Chrome, te tirotiro kē atu rānei.',
  },
  mr: {
    cameraDenied: 'कॅमेरा प्रवेश यापूर्वी नाकारण्यात आला होता.',
    microphoneDenied: 'मायक्रोफोन प्रवेश यापूर्वी नाकारण्यात आला होता.',
    bothDenied: 'कॅमेरा आणि मायक्रोफोन प्रवेश यापूर्वी नाकारण्यात आला होता.',
    resetCameraAndMic:
      'कृपया कॅमेरा आणि मायक्रोफोन परवानग्या रीसेट करण्यासाठी आपल्या ब्राउझर सेटिंग्ज तपासा, त्यानंतर पृष्ठ रिफ्रेश करा.',
    resetCamera:
      'कृपया कॅमेरा परवानग्या रीसेट करण्यासाठी आपल्या ब्राउझर सेटिंग्ज तपासा, त्यानंतर पृष्ठ रिफ्रेश करा.',
    resetMic:
      'कृपया मायक्रोफोन परवानग्या रीसेट करण्यासाठी आपल्या ब्राउझर सेटिंग्ज तपासा, त्यानंतर पृष्ठ रिफ्रेश करा.',
    firefoxAndroidWarning:
      'Firefox Android मध्ये कॅमेरा आणि मायक्रोफोनच्या ज्ञात समस्या आहेत. उत्तम अनुभवासाठी, कृपया Chrome किंवा इतर ब्राउझर वापरा.',
  },
  ne: {
    cameraDenied: 'क्यामेरा पहुँच पहिले अस्वीकृत गरिएको थियो।',
    microphoneDenied: 'माइक्रोफोन पहुँच पहिले अस्वीकृत गरिएको थियो।',
    bothDenied: 'क्यामेरा र माइक्रोफोन पहुँच पहिले अस्वीकृत गरिएको थियो।',
    resetCameraAndMic:
      'कृपया क्यामेरा र माइक्रोफोन अनुमतिहरू पुनःसेट गर्न आफ्नो ब्राउजर सेटिङहरू जाँच गर्नुहोस्, त्यसपछि पृष्ठ ताजा गर्नुहोस्।',
    resetCamera:
      'कृपया क्यामेरा अनुमतिहरू पुनःसेट गर्न आफ्नो ब्राउजर सेटिङहरू जाँच गर्नुहोस्, त्यसपछि पृष्ठ ताजा गर्नुहोस्।',
    resetMic:
      'कृपया माइक्रोफोन अनुमतिहरू पुनःसेट गर्न आफ्नो ब्राउजर सेटिङहरू जाँच गर्नुहोस्, त्यसपछि पृष्ठ ताजा गर्नुहोस्।',
    firefoxAndroidWarning:
      'Firefox Android मा क्यामेरा र माइक्रोफोनका ज्ञात समस्याहरू छन्। उत्कृष्ट अनुभवका लागि, कृपया Chrome वा अर्को ब्राउजर प्रयोग गर्नुहोस्।',
  },
  no: {
    cameraDenied: 'Kameratilgang ble tidligere avvist.',
    microphoneDenied: 'Mikrofontilgang ble tidligere avvist.',
    bothDenied: 'Kamera- og mikrofontilgang ble tidligere avvist.',
    resetCameraAndMic:
      'Sjekk nettleserinnstillingene for å tilbakestille kamera- og mikrofontillatelsene, og oppdater deretter siden.',
    resetCamera:
      'Sjekk nettleserinnstillingene for å tilbakestille kameratillatelsene, og oppdater deretter siden.',
    resetMic:
      'Sjekk nettleserinnstillingene for å tilbakestille mikrofontillatelsene, og oppdater deretter siden.',
    firefoxAndroidWarning:
      'Firefox Android har kjente problemer med kamera og mikrofon. For best mulig opplevelse, bruk Chrome eller en annen nettleser.',
  },
  pa: {
    cameraDenied: 'ਕੈਮਰਾ ਪਹੁੰਚ ਪਹਿਲਾਂ ਤੋਂ ਹੀ ਇਨਕਾਰ ਕੀਤੀ ਗਈ ਸੀ।',
    microphoneDenied: 'ਮਾਈਕ੍ਰੋਫੋਨ ਪਹੁੰਚ ਪਹਿਲਾਂ ਤੋਂ ਹੀ ਇਨਕਾਰ ਕੀਤੀ ਗਈ ਸੀ।',
    bothDenied: 'ਕੈਮਰਾ ਅਤੇ ਮਾਈਕ੍ਰੋਫੋਨ ਪਹੁੰਚ ਪਹਿਲਾਂ ਤੋਂ ਹੀ ਇਨਕਾਰ ਕੀਤੀ ਗਈ ਸੀ।',
    resetCameraAndMic:
      'ਕਿਰਪਾ ਕਰਕੇ ਕੈਮਰਾ ਅਤੇ ਮਾਈਕ੍ਰੋਫੋਨ ਇਜਾਜ਼ਤਾਂ ਨੂੰ ਰੀਸੈੱਟ ਕਰਨ ਲਈ ਆਪਣੇ ਬ੍ਰਾਊਜ਼ਰ ਸੈਟਿੰਗਾਂ ਦੀ ਜਾਂਚ ਕਰੋ, ਫਿਰ ਪੰਨੇ ਨੂੰ ਤਾਜ਼ਾ ਕਰੋ।',
    resetCamera:
      'ਕਿਰਪਾ ਕਰਕੇ ਕੈਮਰਾ ਇਜਾਜ਼ਤਾਂ ਨੂੰ ਰੀਸੈੱਟ ਕਰਨ ਲਈ ਆਪਣੇ ਬ੍ਰਾਊਜ਼ਰ ਸੈਟਿੰਗਾਂ ਦੀ ਜਾਂਚ ਕਰੋ, ਫਿਰ ਪੰਨੇ ਨੂੰ ਤਾਜ਼ਾ ਕਰੋ।',
    resetMic:
      'ਕਿਰਪਾ ਕਰਕੇ ਮਾਈਕ੍ਰੋਫੋਨ ਇਜਾਜ਼ਤਾਂ ਨੂੰ ਰੀਸੈੱਟ ਕਰਨ ਲਈ ਆਪਣੇ ਬ੍ਰਾਊਜ਼ਰ ਸੈਟਿੰਗਾਂ ਦੀ ਜਾਂਚ ਕਰੋ, ਫਿਰ ਪੰਨੇ ਨੂੰ ਤਾਜ਼ਾ ਕਰੋ।',
    firefoxAndroidWarning:
      'Firefox Android ਵਿੱਚ ਕੈਮਰਾ ਅਤੇ ਮਾਈਕ੍ਰੋਫੋਨ ਦੀਆਂ ਜਾਣੀਆਂ-ਪਛਾਣੀਆਂ ਸਮੱਸਿਆਵਾਂ ਹਨ। ਵਧੀਆ ਅਨੁਭਵ ਲਈ, ਕਿਰਪਾ ਕਰਕੇ Chrome ਜਾਂ ਕੋਈ ਹੋਰ ਬ੍ਰਾਊਜ਼ਰ ਵਰਤੋ।',
  },
  fa: {
    cameraDenied: 'دسترسی به دوربین پیش‌تر رد شده است.',
    microphoneDenied: 'دسترسی به میکروفون پیش‌تر رد شده است.',
    bothDenied: 'دسترسی به دوربین و میکروفون پیش‌تر رد شده است.',
    resetCameraAndMic:
      'لطفاً تنظیمات مرورگر خود را برای بازنشانی مجوزهای دوربین و میکروفون بررسی کنید، سپس صفحه را تازه‌سازی کنید.',
    resetCamera:
      'لطفاً تنظیمات مرورگر خود را برای بازنشانی مجوزهای دوربین بررسی کنید، سپس صفحه را تازه‌سازی کنید.',
    resetMic:
      'لطفاً تنظیمات مرورگر خود را برای بازنشانی مجوزهای میکروفون بررسی کنید، سپس صفحه را تازه‌سازی کنید.',
    firefoxAndroidWarning:
      'Firefox Android مشکلات شناخته‌شده‌ای با دوربین و میکروفون دارد. برای بهترین تجربه، لطفاً از Chrome یا مرورگر دیگری استفاده کنید.',
  },
  pl: {
    cameraDenied: 'Dostęp do kamery został wcześniej odrzucony.',
    microphoneDenied: 'Dostęp do mikrofonu został wcześniej odrzucony.',
    bothDenied: 'Dostęp do kamery i mikrofonu został wcześniej odrzucony.',
    resetCameraAndMic:
      'Sprawdź ustawienia przeglądarki, aby zresetować uprawnienia do kamery i mikrofonu, a następnie odśwież stronę.',
    resetCamera:
      'Sprawdź ustawienia przeglądarki, aby zresetować uprawnienia do kamery, a następnie odśwież stronę.',
    resetMic:
      'Sprawdź ustawienia przeglądarki, aby zresetować uprawnienia do mikrofonu, a następnie odśwież stronę.',
    firefoxAndroidWarning:
      'Firefox Android ma znane problemy z kamerą i mikrofonem. Aby zapewnić najlepsze wrażenia, użyj Chrome lub innej przeglądarki.',
  },
  pt: {
    cameraDenied: 'O acesso à câmara foi anteriormente negado.',
    microphoneDenied: 'O acesso ao microfone foi anteriormente negado.',
    bothDenied: 'O acesso à câmara e ao microfone foi anteriormente negado.',
    resetCameraAndMic:
      'Verifique as definições do seu navegador para repor as permissões da câmara e do microfone e, em seguida, atualize a página.',
    resetCamera:
      'Verifique as definições do seu navegador para repor as permissões da câmara e, em seguida, atualize a página.',
    resetMic:
      'Verifique as definições do seu navegador para repor as permissões do microfone e, em seguida, atualize a página.',
    firefoxAndroidWarning:
      'O Firefox Android tem problemas conhecidos com a câmara e o microfone. Para uma melhor experiência, utilize o Chrome ou outro navegador.',
  },
  tl: {
    cameraDenied: 'Dati nang tinanggihan ang akses sa camera.',
    microphoneDenied: 'Dati nang tinanggihan ang akses sa mikropono.',
    bothDenied: 'Dati nang tinanggihan ang akses sa camera at mikropono.',
    resetCameraAndMic:
      'Pakisuri ang mga setting ng iyong browser upang i-reset ang mga pahintulot sa camera at mikropono, pagkatapos ay i-refresh ang pahina.',
    resetCamera:
      'Pakisuri ang mga setting ng iyong browser upang i-reset ang mga pahintulot sa camera, pagkatapos ay i-refresh ang pahina.',
    resetMic:
      'Pakisuri ang mga setting ng iyong browser upang i-reset ang mga pahintulot sa mikropono, pagkatapos ay i-refresh ang pahina.',
    firefoxAndroidWarning:
      'May kilalang mga problema sa camera at mikropono ang Firefox Android. Para sa pinakamahusay na karanasan, mangyaring gamitin ang Chrome o ibang browser.',
  },
  ro: {
    cameraDenied: 'Accesul la cameră a fost refuzat anterior.',
    microphoneDenied: 'Accesul la microfon a fost refuzat anterior.',
    bothDenied: 'Accesul la cameră și microfon a fost refuzat anterior.',
    resetCameraAndMic:
      'Verificați setările browserului pentru a reseta permisiunile pentru cameră și microfon, apoi reîmprospătați pagina.',
    resetCamera:
      'Verificați setările browserului pentru a reseta permisiunile pentru cameră, apoi reîmprospătați pagina.',
    resetMic:
      'Verificați setările browserului pentru a reseta permisiunile pentru microfon, apoi reîmprospătați pagina.',
    firefoxAndroidWarning:
      'Firefox Android are probleme cunoscute cu camera și microfonul. Pentru cea mai bună experiență, utilizați Chrome sau alt browser.',
  },
  ru: {
    cameraDenied: 'Доступ к камере был ранее запрещён.',
    microphoneDenied: 'Доступ к микрофону был ранее запрещён.',
    bothDenied: 'Доступ к камере и микрофону был ранее запрещён.',
    resetCameraAndMic:
      'Пожалуйста, проверьте настройки браузера, чтобы сбросить разрешения для камеры и микрофона, а затем обновите страницу.',
    resetCamera:
      'Пожалуйста, проверьте настройки браузера, чтобы сбросить разрешения для камеры, а затем обновите страницу.',
    resetMic:
      'Пожалуйста, проверьте настройки браузера, чтобы сбросить разрешения для микрофона, а затем обновите страницу.',
    firefoxAndroidWarning:
      'В Firefox Android есть известные проблемы с камерой и микрофоном. Для наилучшего качества работы используйте Chrome или другой браузер.',
  },
  sr: {
    cameraDenied: 'Pristup kameri je prethodno odbijen.',
    microphoneDenied: 'Pristup mikrofonu je prethodno odbijen.',
    bothDenied: 'Pristup kameri i mikrofonu je prethodno odbijen.',
    resetCameraAndMic:
      'Molimo proverite podešavanja pregledača da biste resetovali dozvole za kameru i mikrofon, a zatim osvežite stranicu.',
    resetCamera:
      'Molimo proverite podešavanja pregledača da biste resetovali dozvole za kameru, a zatim osvežite stranicu.',
    resetMic:
      'Molimo proverite podešavanja pregledača da biste resetovali dozvole za mikrofon, a zatim osvežite stranicu.',
    firefoxAndroidWarning:
      'Firefox Android ima poznate probleme sa kamerom i mikrofonom. Za najbolje iskustvo, koristite Chrome ili drugi pregledač.',
  },
  sk: {
    cameraDenied: 'Prístup ku kamere bol predtým zamietnutý.',
    microphoneDenied: 'Prístup k mikrofónu bol predtým zamietnutý.',
    bothDenied: 'Prístup ku kamere a mikrofónu bol predtým zamietnutý.',
    resetCameraAndMic:
      'Skontrolujte, prosím, nastavenia prehliadača a obnovte povolenia pre kameru a mikrofón, potom znova načítajte stránku.',
    resetCamera:
      'Skontrolujte, prosím, nastavenia prehliadača a obnovte povolenia pre kameru, potom znova načítajte stránku.',
    resetMic:
      'Skontrolujte, prosím, nastavenia prehliadača a obnovte povolenia pre mikrofón, potom znova načítajte stránku.',
    firefoxAndroidWarning:
      'Firefox Android má známe problémy s kamerou a mikrofónom. Pre najlepší zážitok použite Chrome alebo iný prehliadač.',
  },
  sl: {
    cameraDenied: 'Dostop do kamere je bil predhodno zavrnjen.',
    microphoneDenied: 'Dostop do mikrofona je bil predhodno zavrnjen.',
    bothDenied: 'Dostop do kamere in mikrofona je bil predhodno zavrnjen.',
    resetCameraAndMic:
      'Preverite nastavitve brskalnika in ponastavite dovoljenja za kamero in mikrofon, nato osvežite stran.',
    resetCamera:
      'Preverite nastavitve brskalnika in ponastavite dovoljenja za kamero, nato osvežite stran.',
    resetMic:
      'Preverite nastavitve brskalnika in ponastavite dovoljenja za mikrofon, nato osvežite stran.',
    firefoxAndroidWarning:
      'Firefox Android ima znane težave s kamero in mikrofonom. Za najboljšo izkušnjo uporabite Chrome ali drug brskalnik.',
  },
  es: {
    cameraDenied: 'Se denegó previamente el acceso a la cámara.',
    microphoneDenied: 'Se denegó previamente el acceso al micrófono.',
    bothDenied: 'Se denegó previamente el acceso a la cámara y al micrófono.',
    resetCameraAndMic:
      'Comprueba la configuración de tu navegador para restablecer los permisos de la cámara y del micrófono, y luego actualiza la página.',
    resetCamera:
      'Comprueba la configuración de tu navegador para restablecer los permisos de la cámara, y luego actualiza la página.',
    resetMic:
      'Comprueba la configuración de tu navegador para restablecer los permisos del micrófono, y luego actualiza la página.',
    firefoxAndroidWarning:
      'Firefox Android presenta problemas conocidos con la cámara y el micrófono. Para una mejor experiencia, utiliza Chrome u otro navegador.',
  },
  sv: {
    cameraDenied: 'Åtkomst till kameran nekades tidigare.',
    microphoneDenied: 'Åtkomst till mikrofonen nekades tidigare.',
    bothDenied: 'Åtkomst till kameran och mikrofonen nekades tidigare.',
    resetCameraAndMic:
      'Kontrollera webbläsarens inställningar för att återställa behörigheterna för kamera och mikrofon, och uppdatera sedan sidan.',
    resetCamera:
      'Kontrollera webbläsarens inställningar för att återställa behörigheterna för kameran, och uppdatera sedan sidan.',
    resetMic:
      'Kontrollera webbläsarens inställningar för att återställa behörigheterna för mikrofonen, och uppdatera sedan sidan.',
    firefoxAndroidWarning:
      'Firefox Android har kända problem med kamera och mikrofon. För bästa upplevelse, använd Chrome eller en annan webbläsare.',
  },
  sw: {
    cameraDenied: 'Ufikiaji wa kamera ulikataliwa hapo awali.',
    microphoneDenied: 'Ufikiaji wa maikrofoni ulikataliwa hapo awali.',
    bothDenied: 'Ufikiaji wa kamera na maikrofoni ulikataliwa hapo awali.',
    resetCameraAndMic:
      'Tafadhali angalia mipangilio ya kivinjari chako ili kuweka upya ruhusa za kamera na maikrofoni, kisha upakie ukurasa tena.',
    resetCamera:
      'Tafadhali angalia mipangilio ya kivinjari chako ili kuweka upya ruhusa za kamera, kisha upakie ukurasa tena.',
    resetMic:
      'Tafadhali angalia mipangilio ya kivinjari chako ili kuweka upya ruhusa za maikrofoni, kisha upakie ukurasa tena.',
    firefoxAndroidWarning:
      'Firefox Android ina matatizo yanayojulikana ya kamera na maikrofoni. Kwa matumizi bora, tafadhali tumia Chrome au kivinjari kingine.',
  },
  ta: {
    cameraDenied: 'கேமரா அணுகல் முன்பே மறுக்கப்பட்டது.',
    microphoneDenied: 'மைக்ரோஃபோன் அணுகல் முன்பே மறுக்கப்பட்டது.',
    bothDenied: 'கேமரா மற்றும் மைக்ரோஃபோன் அணுகல் முன்பே மறுக்கப்பட்டது.',
    resetCameraAndMic:
      'தயவுசெய்து உங்கள் உலாவி அமைப்புகளைச் சரிபார்த்து, கேமரா மற்றும் மைக்ரோஃபோன் அனுமதிகளை மீட்டமைத்து, பின்னர் பக்கத்தைப் புதுப்பிக்கவும்.',
    resetCamera:
      'தயவுசெய்து உங்கள் உலாவி அமைப்புகளைச் சரிபார்த்து, கேமரா அனுமதிகளை மீட்டமைத்து, பின்னர் பக்கத்தைப் புதுப்பிக்கவும்.',
    resetMic:
      'தயவுசெய்து உங்கள் உலாவி அமைப்புகளைச் சரிபார்த்து, மைக்ரோஃபோன் அனுமதிகளை மீட்டமைத்து, பின்னர் பக்கத்தைப் புதுப்பிக்கவும்.',
    firefoxAndroidWarning:
      'Firefox Android இல் கேமரா மற்றும் மைக்ரோஃபோன் தொடர்பான அறியப்பட்ட சிக்கல்கள் உள்ளன. சிறந்த அனுபவத்திற்கு, Chrome அல்லது வேறு உலாவியைப் பயன்படுத்தவும்.',
  },
  th: {
    cameraDenied: 'การเข้าถึงกล้องถูกปฏิเสธไปก่อนหน้านี้',
    microphoneDenied: 'การเข้าถึงไมโครโฟนถูกปฏิเสธไปก่อนหน้านี้',
    bothDenied: 'การเข้าถึงกล้องและไมโครโฟนถูกปฏิเสธไปก่อนหน้านี้',
    resetCameraAndMic:
      'โปรดตรวจสอบการตั้งค่าเบราว์เซอร์เพื่อรีเซ็ตสิทธิ์การเข้าถึงกล้องและไมโครโฟน จากนั้นรีเฟรชหน้าเว็บ',
    resetCamera:
      'โปรดตรวจสอบการตั้งค่าเบราว์เซอร์เพื่อรีเซ็ตสิทธิ์การเข้าถึงกล้อง จากนั้นรีเฟรชหน้าเว็บ',
    resetMic:
      'โปรดตรวจสอบการตั้งค่าเบราว์เซอร์เพื่อรีเซ็ตสิทธิ์การเข้าถึงไมโครโฟน จากนั้นรีเฟรชหน้าเว็บ',
    firefoxAndroidWarning:
      'Firefox Android มีปัญหาเกี่ยวกับกล้องและไมโครโฟนที่ทราบกันดี เพื่อประสบการณ์ที่ดีที่สุด โปรดใช้ Chrome หรือเบราว์เซอร์อื่น',
  },
  tr: {
    cameraDenied: 'Kamera erişimi daha önce reddedildi.',
    microphoneDenied: 'Mikrofon erişimi daha önce reddedildi.',
    bothDenied: 'Kamera ve mikrofon erişimi daha önce reddedildi.',
    resetCameraAndMic:
      'Lütfen kamera ve mikrofon izinlerini sıfırlamak için tarayıcı ayarlarınızı kontrol edin ve ardından sayfayı yenileyin.',
    resetCamera:
      'Lütfen kamera izinlerini sıfırlamak için tarayıcı ayarlarınızı kontrol edin ve ardından sayfayı yenileyin.',
    resetMic:
      'Lütfen mikrofon izinlerini sıfırlamak için tarayıcı ayarlarınızı kontrol edin ve ardından sayfayı yenileyin.',
    firefoxAndroidWarning:
      'Firefox Android\'de bilinen kamera ve mikrofon sorunları bulunmaktadır. En iyi deneyim için lütfen Chrome veya başka bir tarayıcı kullanın.',
  },
  uk: {
    cameraDenied: 'Доступ до камери було раніше заборонено.',
    microphoneDenied: 'Доступ до мікрофона було раніше заборонено.',
    bothDenied: 'Доступ до камери та мікрофона було раніше заборонено.',
    resetCameraAndMic:
      'Будь ласка, перевірте налаштування браузера, щоб скинути дозволи для камери та мікрофона, а потім оновіть сторінку.',
    resetCamera:
      'Будь ласка, перевірте налаштування браузера, щоб скинути дозволи для камери, а потім оновіть сторінку.',
    resetMic:
      'Будь ласка, перевірте налаштування браузера, щоб скинути дозволи для мікрофона, а потім оновіть сторінку.',
    firefoxAndroidWarning:
      'Firefox Android має відомі проблеми з камерою та мікрофоном. Для найкращої роботи використовуйте Chrome або інший браузер.',
  },
  ur: {
    cameraDenied: 'کیمرے تک رسائی پہلے مسترد کر دی گئی تھی۔',
    microphoneDenied: 'مائیکروفون تک رسائی پہلے مسترد کر دی گئی تھی۔',
    bothDenied: 'کیمرے اور مائیکروفون تک رسائی پہلے مسترد کر دی گئی تھی۔',
    resetCameraAndMic:
      'براہ کرم کیمرے اور مائیکروفون کی اجازتیں دوبارہ ترتیب دینے کے لیے اپنے براؤزر کی سیٹنگز چیک کریں، پھر صفحہ ریفریش کریں۔',
    resetCamera:
      'براہ کرم کیمرے کی اجازتیں دوبارہ ترتیب دینے کے لیے اپنے براؤزر کی سیٹنگز چیک کریں، پھر صفحہ ریفریش کریں۔',
    resetMic:
      'براہ کرم مائیکروفون کی اجازتیں دوبارہ ترتیب دینے کے لیے اپنے براؤزر کی سیٹنگز چیک کریں، پھر صفحہ ریفریش کریں۔',
    firefoxAndroidWarning:
      'Firefox Android میں کیمرے اور مائیکروفون کے معلوم مسائل ہیں۔ بہترین تجربے کے لیے، براہ کرم Chrome یا کوئی دوسرا براؤزر استعمال کریں۔',
  },
  uz: {
    cameraDenied: 'Kameraga kirish ilgari rad etilgan.',
    microphoneDenied: 'Mikrofonga kirish ilgari rad etilgan.',
    bothDenied: 'Kamera va mikrofonga kirish ilgari rad etilgan.',
    resetCameraAndMic:
      'Iltimos, kamera va mikrofon ruxsatlarini qayta tiklash uchun brauzer sozlamalarini tekshiring, so\'ngra sahifani yangilang.',
    resetCamera:
      'Iltimos, kamera ruxsatlarini qayta tiklash uchun brauzer sozlamalarini tekshiring, so\'ngra sahifani yangilang.',
    resetMic:
      'Iltimos, mikrofon ruxsatlarini qayta tiklash uchun brauzer sozlamalarini tekshiring, so\'ngra sahifani yangilang.',
    firefoxAndroidWarning:
      'Firefox Android\'da kamera va mikrofon bilan bog\'liq ma\'lum muammolar mavjud. Eng yaxshi tajriba uchun Chrome yoki boshqa brauzerdan foydalaning.',
  },
  vi: {
    cameraDenied: 'Quyền truy cập máy ảnh đã bị từ chối trước đó.',
    microphoneDenied: 'Quyền truy cập micrô đã bị từ chối trước đó.',
    bothDenied: 'Quyền truy cập máy ảnh và micrô đã bị từ chối trước đó.',
    resetCameraAndMic:
      'Vui lòng kiểm tra cài đặt trình duyệt để đặt lại quyền máy ảnh và micrô, sau đó làm mới trang.',
    resetCamera:
      'Vui lòng kiểm tra cài đặt trình duyệt để đặt lại quyền máy ảnh, sau đó làm mới trang.',
    resetMic:
      'Vui lòng kiểm tra cài đặt trình duyệt để đặt lại quyền micrô, sau đó làm mới trang.',
    firefoxAndroidWarning:
      'Firefox Android có các sự cố đã biết về máy ảnh và micrô. Để có trải nghiệm tốt nhất, vui lòng sử dụng Chrome hoặc trình duyệt khác.',
  },
  cy: {
    cameraDenied: 'Gwrthodwyd mynediad at y camera yn flaenorol.',
    microphoneDenied: 'Gwrthodwyd mynediad at y meicroffon yn flaenorol.',
    bothDenied: 'Gwrthodwyd mynediad at y camera a\'r meicroffon yn flaenorol.',
    resetCameraAndMic:
      'Gwiriwch osodiadau eich porwr i ailosod caniatâd y camera a\'r meicroffon, ac yna adnewyddwch y dudalen.',
    resetCamera:
      'Gwiriwch osodiadau eich porwr i ailosod caniatâd y camera, ac yna adnewyddwch y dudalen.',
    resetMic:
      'Gwiriwch osodiadau eich porwr i ailosod caniatâd y meicroffon, ac yna adnewyddwch y dudalen.',
    firefoxAndroidWarning:
      'Mae gan Firefox Android broblemau hysbys gyda\'r camera a\'r meicroffon. I gael y profiad gorau, defnyddiwch Chrome neu borwr arall.',
  },
  id: {
    cameraDenied: 'Akses kamera sebelumnya ditolak.',
    microphoneDenied: 'Akses mikrofon sebelumnya ditolak.',
    bothDenied: 'Akses kamera dan mikrofon sebelumnya ditolak.',
    resetCameraAndMic:
      'Silakan periksa pengaturan browser Anda untuk mengatur ulang izin kamera dan mikrofon, lalu muat ulang halaman.',
    resetCamera:
      'Silakan periksa pengaturan browser Anda untuk mengatur ulang izin kamera, lalu muat ulang halaman.',
    resetMic:
      'Silakan periksa pengaturan browser Anda untuk mengatur ulang izin mikrofon, lalu muat ulang halaman.',
    firefoxAndroidWarning:
      'Firefox Android memiliki masalah kamera dan mikrofon yang diketahui. Untuk pengalaman terbaik, silakan gunakan Chrome atau browser lain.',
  },
} satisfies Record<TranslationLanguage, PermissionStateTranslation>;

/**
 * Non-respondent-facing technical error strings. Left English-only for
 * now because no consumer renders them in the respondent UI — they
 * exist as identifiers that match `MediaErrorCode` values. If any of
 * these ever start getting shown to respondents, promote them into
 * `PermissionStateTranslation` and translate across all 64 languages.
 */
export const PERMISSION_MESSAGES_INTERNAL = {
  CRITICAL: {
    BROWSER_NOT_SUPPORTED: 'Browser does not support media recording features',
    CAMERA_NOT_FOUND: 'No camera device found',
    MICROPHONE_NOT_FOUND: 'No microphone device found',
    GETUSERMEDIA_UNAVAILABLE: 'Media access API not available',
  },
  BROWSER_SPECIFIC: {
    IOS_CHROME_LIMITED:
      'Chrome on iOS has limited media recording capabilities. Use Safari for better experience.',
    IOS_SAFARI_UNRELIABLE: 'iOS Safari permission states may be unreliable',
    FIREFOX_MOBILE_PROMPTS: 'Firefox mobile may require additional permission prompts',
  },
} as const;

/**
 * Back-compat re-export. The previous flat `PERMISSION_MESSAGES` object
 * had `PERMISSION_STATE.*` strings + a `RESET_INSTRUCTIONS` function;
 * new code should use `PERMISSION_STATE_TRANSLATION` + the helpers in
 * permission-checker.ts instead.
 */
export const PERMISSION_MESSAGES = {
  ...PERMISSION_MESSAGES_INTERNAL,
  PERMISSION_STATE: {
    CAMERA_DENIED: PERMISSION_STATE_TRANSLATION.en.cameraDenied,
    MICROPHONE_DENIED: PERMISSION_STATE_TRANSLATION.en.microphoneDenied,
    BOTH_DENIED: PERMISSION_STATE_TRANSLATION.en.bothDenied,
    RESET_INSTRUCTIONS: (hasVideoDenied: boolean, hasAudioDenied: boolean): string => {
      const t = PERMISSION_STATE_TRANSLATION.en;
      if (hasVideoDenied && hasAudioDenied) return t.resetCameraAndMic;
      if (hasVideoDenied) return t.resetCamera;
      if (hasAudioDenied) return t.resetMic;
      return '';
    },
  },
} as const;
